import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

/**
 * Reads a PostHog feature flag.
 *
 * Flags arrive asynchronously after posthog.init, so this starts false and flips once
 * they load. That means a gated feature stays hidden until PostHog has answered, which
 * is the safe default: nothing appears for users the flag does not cover, and nothing
 * appears at all if PostHog is unreachable or NEXT_PUBLIC_POSTHOG_KEY is unset.
 *
 * Targeting lives in PostHog's release conditions rather than in code, so who sees a
 * feature can change without a deploy. Users are identified by email
 * (see HOCs/protectAuth.jsx), so conditions can match on the email person property.
 *
 * @param {string} flagKey PostHog feature flag key.
 * @returns {boolean} Whether the flag is enabled for the current user.
 */
const useFeatureFlag = (flagKey) => {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    if (!flagKey || typeof window === 'undefined') return undefined;

    const read = () => {
      // isFeatureEnabled returns undefined until flags have loaded.
      setIsEnabled(posthog.isFeatureEnabled?.(flagKey) === true);
    };

    read();

    // Fires when flags first load and whenever they are re-evaluated, e.g. after the
    // user is identified, which is when email-based conditions start matching.
    const unsubscribe = posthog.onFeatureFlags?.(read);
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [flagKey]);

  return isEnabled;
};

export default useFeatureFlag;
